// notificationsApis.tsx
import axios, { AxiosResponse } from "axios";
import { getApiBaseUrl } from "src/config/Endpoints";
import type { InAppNotification } from "src/layouts/MyAccount/Notifications/NotificationInterfaces";

const API_BASE_URL = getApiBaseUrl();

export const getApiKey = (): string | null => localStorage.getItem("api_key");
export const getUserId = (): string | null => localStorage.getItem("user_id");

const buildQuery = (params: Record<string, string | undefined>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {query.set(key, value);}
  });
  const q = query.toString();
  return q ? `?${q}` : "";
};

// --- Mark Notification as Read ---
// PUT /api/notifications/in-app/mark-read
export const markNotificationAsRead = async (
  notification_id: number
): Promise<boolean> => {
  const apiKey = getApiKey();

  if (!apiKey || !notification_id) {
    throw new Error("Missing API key or notification ID.");
  }

  const response: AxiosResponse<{ status: boolean }> = await axios.put(
    `${API_BASE_URL}/notifications/in-app/mark-read`,
    { notification_ids: [notification_id] },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data.status;
};

// --- Unread Count ---
// GET /api/notifications/in-app/unread-count
export const getTotalUnreadNotifications = async (): Promise<number> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Missing API key in localStorage.");
  }

  const response: AxiosResponse<{
    status: boolean;
    unread_count: number;
  }> = await axios.get(`${API_BASE_URL}/notifications/in-app/unread-count`, {
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
  });

  if (response.data.status) {
    localStorage.setItem(
      "total_unread_count",
      String(response.data.unread_count)
    );
    return response.data.unread_count;
  } else {
    throw new Error("Invalid response from unread notifications API.");
  }
};

// --- Fetch In-App Notifications ---
// GET /api/notifications/in-app
export interface InAppNotificationPagination {
  page: number;
  // Null when the response is unpaginated (neither `page` nor `page_size` was sent).
  pageSize: number | null;
  totalRecords: number;
  totalPages: number;
}

export interface InAppNotificationDateFilter {
  date_range: string;
  start_date: string;
  end_date: string;
}

export interface InAppNotificationResponse {
  status: boolean;
  unread_count: number;
  date_filter: InAppNotificationDateFilter | null;
  pagination: InAppNotificationPagination;
  notifications: InAppNotification[];
}

export const fetchInAppNotificationsFull = async (params?: {
  unread_only?: boolean;
  page?: number;
  page_size?: number;
  date_range?: "today" | "this_week" | "last_week";
  start_date?: string;
  end_date?: string;
  lang?: string | null;
}): Promise<InAppNotificationResponse> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Missing API key in localStorage.");
  }

  const query = new URLSearchParams();
  if (params?.unread_only !== undefined) {
    query.set("unread_only", String(params.unread_only));
  }
  if (params?.page !== undefined) {
    query.set("page", String(params.page));
  }
  if (params?.page_size !== undefined) {
    query.set("page_size", String(params.page_size));
  }
  if (params?.date_range) {
    query.set("date_range", params.date_range);
  }
  if (params?.start_date) {
    query.set("start_date", params.start_date);
  }
  if (params?.end_date) {
    query.set("end_date", params.end_date);
  }
  if (params?.lang) {
    query.set("lang", params.lang);
  }

  const queryString = query.toString();
  const suffix = queryString ? `?${queryString}` : "";
  const response: AxiosResponse<InAppNotificationResponse> = await axios.get(
    `${API_BASE_URL}/notifications/in-app${suffix}`,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    }
  );

  if (!response.data.status) {
    throw new Error("Invalid response from in-app notifications API.");
  }

  return response.data;
};

export const fetchInAppNotifications = async (params?: {
  unread_only?: boolean;
  page?: number;
  page_size?: number;
  date_range?: "today" | "this_week" | "last_week";
  start_date?: string;
  end_date?: string;
  lang?: string | null;
}): Promise<InAppNotification[]> => {
  const data = await fetchInAppNotificationsFull(params);
  return data.notifications;
};

export const markAllNotificationsAsRead = async (lang?: string | null): Promise<boolean> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Missing API key in localStorage.");
  }

  const { notifications } = await fetchInAppNotificationsFull({
    unread_only: true,
    lang,
  });

  if (notifications.length === 0) {
    return true;
  }

  const response: AxiosResponse<{ status: boolean }> = await axios.put(
    `${API_BASE_URL}/notifications/in-app/mark-read`,
    { notification_ids: notifications.map(n => n.notification_id) },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data.status;
};

// --- Get Announcements for Users ---
export const getUserAnnouncements = async (): Promise<
  { title: string; content: string; url?: string }[]
> => {
  const apiKey = getApiKey();
  const userId = getUserId();
  const tenantId = localStorage.getItem("tenant_id");

  if (!apiKey || !userId || !tenantId) {
    throw new Error("Missing required credentials in localStorage.");
  }

  const response = await axios.post(
    `${API_BASE_URL}/user/announcements`,
    {},
    {
      params: {
        user_id: Number(userId),
        tenant_id: Number(tenantId),
      },
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data || [];
};

// ---- Rule CRUD helpers ----

const authHeaders = () => {
  const apiKey = getApiKey();
  if (!apiKey) {throw new Error("Missing API key in localStorage.");}
  return { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" };
};

// --- Notification channel masters ---
export interface NotificationChannelMastersResponse {
  status: boolean;
  channel_masters: { email: boolean; push: boolean; in_app: boolean };
  changes: Record<
    string,
    {
      enabled: boolean;
      rules_changed: number;
      action:
        | "disabled"
        | "restored"
        | "enabled_all_configured"
        | "no_configured_rules"
        | "no_change";
    }
  >;
}

export const updateNotificationChannelMasters = async (
  payload: Partial<{ email: boolean; push: boolean; in_app: boolean }>
): Promise<NotificationChannelMastersResponse> => {
  const response = await axios.put(
    `${API_BASE_URL}/notifications/channels`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

// ---- Paid Media ----

export const fetchPaidMediaPlatforms = async (): Promise<{
  status: boolean;
  platforms: { key: string; label: string }[];
}> => {
  const response = await axios.get(
    `${API_BASE_URL}/notifications/paid-media/platforms`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const fetchPaidMediaCampaigns = async (
  platforms: string[],
  search?: string
): Promise<{
  status: boolean;
  campaigns: { platform: string; platform_label: string; campaign_id: string; campaign_name: string }[];
}> => {
  const query = new URLSearchParams();
  if (platforms.length) {query.set("platforms", platforms.join(","));}
  if (search?.trim()) {query.set("search", search.trim());}
  const response = await axios.get(
    `${API_BASE_URL}/notifications/paid-media/campaigns?${query.toString()}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const createPaidMediaRule = async (payload: Record<string, unknown>): Promise<{ status: boolean; rule: unknown }> => {
  const response = await axios.post(
    `${API_BASE_URL}/notifications/paid-media/rules`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const updatePaidMediaRule = async (
  ruleId: number,
  payload: Record<string, unknown>
): Promise<{ status: boolean }> => {
  const response = await axios.put(
    `${API_BASE_URL}/notifications/paid-media/rules/${ruleId}`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const deletePaidMediaRule = async (ruleId: number): Promise<{ status: boolean; message: string }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/notifications/paid-media/rules/${ruleId}`,
    { headers: authHeaders() }
  );
  return response.data;
};

// ---- Social ----

export const fetchSocialPlatforms = async (lang?: string): Promise<{
  status: boolean;
  platforms: {
    key: string;
    label: string;
    available: boolean;
    account_name: string;
    pages: { platform: string; platform_label: string; page_id: string; page_name: string }[];
  }[];
}> => {
  const response = await axios.get(
    `${API_BASE_URL}/notifications/social/platforms${buildQuery({ lang })}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const createSocialRule = async (payload: Record<string, unknown>): Promise<{ status: boolean; rule: unknown }> => {
  const response = await axios.post(
    `${API_BASE_URL}/notifications/social/rules`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const updateSocialRule = async (
  ruleId: number,
  payload: Record<string, unknown>
): Promise<{ status: boolean }> => {
  const response = await axios.put(
    `${API_BASE_URL}/notifications/social/rules/${ruleId}`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const deleteSocialRule = async (ruleId: number): Promise<{ status: boolean; message: string }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/notifications/social/rules/${ruleId}`,
    { headers: authHeaders() }
  );
  return response.data;
};

// ---- Web Analytics ----
export interface CitiesResponse {
  status: boolean;
  cities: string[];
  available: boolean;
}

export const fetchWebAnalyticsCities = async (
  search?: string,
  lang?: string
): Promise<CitiesResponse> => {
  const query = buildQuery({ search: search?.trim(), lang });
  const response = await axios.get(
    `${API_BASE_URL}/notifications/web-analytics/cities${query}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const createWebAnalyticsRule = async (payload: Record<string, unknown>): Promise<{ status: boolean; rule: unknown }> => {
  const response = await axios.post(
    `${API_BASE_URL}/notifications/web-analytics/rules`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const updateWebAnalyticsRule = async (
  ruleId: number,
  payload: Record<string, unknown>
): Promise<{ status: boolean }> => {
  const response = await axios.put(
    `${API_BASE_URL}/notifications/web-analytics/rules/${ruleId}`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const deleteWebAnalyticsRule = async (ruleId: number): Promise<{ status: boolean; message: string }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/notifications/web-analytics/rules/${ruleId}`,
    { headers: authHeaders() }
  );
  return response.data;
};

// ---- Sales (Track Sales Performance) ----

export const fetchSalesMetrics = async (): Promise<{
  status: boolean;
  metrics: { key: string; label: string; unit: string; kind: string }[];
}> => {
  const response = await axios.get(
    `${API_BASE_URL}/notifications/sales/metrics`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const fetchSalesCities = async (
  search?: string,
  lang?: string
): Promise<CitiesResponse> => {
  const query = buildQuery({ search: search?.trim(), lang });
  const response = await axios.get(
    `${API_BASE_URL}/notifications/sales/cities${query}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export interface SalesProductsResponse {
  status: boolean;
  products: { product_id: string; product_name: string }[];
  available: boolean;
}

export const fetchSalesProducts = async (
  search?: string,
  lang?: string
): Promise<SalesProductsResponse> => {
  const query = buildQuery({ search: search?.trim(), lang });
  const response = await axios.get(
    `${API_BASE_URL}/notifications/sales/products${query}`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const createSalesRule = async (payload: Record<string, unknown>): Promise<{ status: boolean; rule: unknown }> => {
  const response = await axios.post(
    `${API_BASE_URL}/notifications/sales/rules`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const updateSalesRule = async (
  ruleId: number,
  payload: Record<string, unknown>
): Promise<{ status: boolean }> => {
  const response = await axios.put(
    `${API_BASE_URL}/notifications/sales/rules/${ruleId}`,
    payload,
    { headers: authHeaders() }
  );
  return response.data;
};

export const deleteSalesRule = async (ruleId: number): Promise<{ status: boolean; message: string }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/notifications/sales/rules/${ruleId}`,
    { headers: authHeaders() }
  );
  return response.data;
};
